import { useMe, useNodes } from "../api.ts";
import { Block, Card, Pill } from "../calm.tsx";
import { RUNTIME_LABEL } from "../lib/run-runtime.ts";
import { nextSeatCommand, SEAT_RUNTIMES, SEAT_STATE_LABEL, seatState, seatStatus, type SeatState } from "../lib/seats.ts";

const TONE: Record<SeatState, "live" | "signal" | "neutral" | "danger"> = {
  ready: "live",
  connect: "signal",
  needs_keychain: "danger",
  unsupported: "neutral",
  not_installed: "neutral",
  unknown: "neutral",
};

/** Live "Seats on this node" panel (bring-your-own-seats §4.2): state chip and next command per runtime, from `NodeDTO.runtimes`.
 *  Shows only what the node reports: a version, a state and a command. Never a path or a credential. */
export function SeatsPanel() {
  const me = useMe().data;
  const nodes = (useNodes().data ?? []).filter((n) => n.role === "member" && n.memberId === me?.id);
  return (
    <Block title="Seats on this node" aside="Which subscriptions your node can run">
      <div className="space-y-4" data-testid="seats-panel">
        {nodes.length === 0 && (
          <Card><p className="text-[13px] text-ink-3" data-testid="seats-empty">No node of yours has connected yet. Follow the guide below, then your seats appear here.</p></Card>
        )}
        {nodes.map((node) => (
          <Card key={node.id} pad={false}>
            <div className="flex items-center justify-between gap-3 border-b border-rule px-5 py-3 text-[13px]">
              <span className="font-medium text-ink">{node.name}</span>
              <Pill tone={node.online ? "live" : "neutral"}>{node.online ? "online" : "offline"}</Pill>
            </div>
            {node.runtimes === null ? (
              <p className="px-5 py-4 text-[13px] text-ink-3" data-testid={`seats-unreported-${node.id}`}>This node has not reported its runtimes. Update it to see its seats.</p>
            ) : (
              <ul className="divide-y divide-rule/60">
                {SEAT_RUNTIMES.map((runtime) => {
                  const status = seatStatus(node, runtime);
                  const state = seatState(status);
                  const command = nextSeatCommand(runtime, state);
                  return (
                    <li key={runtime} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-5 py-3 text-[13px]" data-testid={`seat-${node.id}-${runtime}`}>
                      <span className="w-28 font-medium text-ink">{RUNTIME_LABEL[runtime]}</span>
                      <Pill tone={TONE[state]} testId={`seat-chip-${node.id}-${runtime}`}>{SEAT_STATE_LABEL[state]}</Pill>
                      {status?.version && <span className="font-mono text-[12px] text-ink-3">{status.version}</span>}
                      {status && !status.ready && status.blocker && <span className="text-[12px] text-ink-3">{status.blocker}</span>}
                      {command && <code className="ml-auto rounded-md border border-rule bg-paper-2 px-2 py-1 font-mono text-[12px] text-ink" data-testid={`seat-command-${node.id}-${runtime}`}>{command}</code>}
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        ))}
      </div>
    </Block>
  );
}
